import type { EChartsOption } from 'echarts';

const SUFFIXES: [number, string][] = [
  [1_000_000, 'm'],
  [1_000, 'k'],
];

export function formatCompact(n: number): string {
  for (const [threshold, suffix] of SUFFIXES) {
    if (n >= threshold) {
      const val = n / threshold;
      const formatted = val % 1 === 0 ? val.toFixed(0) : val.toFixed(1).replace(/\.0$/, '');
      return formatted + suffix;
    }
  }
  return n.toLocaleString('ru-RU');
}

export function resolveColor(cssValue: string): string {
  const match = cssValue.match(/^var\((.+)\)$/);
  if (!match) return cssValue;
  return getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim() || cssValue;
}

export function resolveCssVar(name: string, fallback: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

// Серые/тёмные цвета плохо читаются на тёмном фоне — подменяем на text-secondary
function isLowContrast(hex: string): boolean {
  const m = hex.match(/^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i);
  if (!m) return false;
  const lum = (0.299 * parseInt(m[1], 16) + 0.587 * parseInt(m[2], 16) + 0.114 * parseInt(m[3], 16)) / 255;
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  return isDark ? lum < 0.35 : lum > 0.8;
}

export function buildOption(
  labels: string[],
  values: number[],
  colors: string[],
  disabledIndices: Set<number>,
): EChartsOption {
  const activeData = labels
    .map((label, i) => ({ label, value: values[i], color: colors[i] }))
    .filter((_, i) => !disabledIndices.has(i));

  const total = activeData.reduce((sum, d) => sum + d.value, 0);
  const trackColor = resolveCssVar('--rd-chart-track', '#E8E8E8');

  return {
    series: [
      {
        type: 'pie',
        radius: ['55%', '70%'],
        center: ['50%', '50%'],
        silent: true,
        animation: false,
        label: { show: false },
        labelLine: { show: false },
        emphasis: { disabled: true },
        data: [{ value: 1, itemStyle: { color: trackColor } }],
        z: 1,
      },
      {
        type: 'pie',
        radius: ['55%', '70%'],
        center: ['50%', '50%'],
        startAngle: 135,
        avoidLabelOverlap: true,
        padAngle: activeData.length > 1 ? 2 : 0,
        itemStyle: { borderRadius: 6 },
        label: {
          show: true,
          formatter: ({ name, value }: any) => `${name}: ${formatCompact(value)}`,
          fontFamily: '"Inter", sans-serif',
          fontSize: 12,
          fontWeight: 500,
          alignTo: 'labelLine',
        },
        labelLine: {
          show: true,
          length: 12,
          length2: 8,
          smooth: false,
        },
        labelLayout: { hideOverlap: true },
        emphasis: { scale: true, scaleSize: 4 },
        animationDuration: 900,
        animationEasing: 'cubicOut',
        data: activeData.map(d => {
          const labelColor = isLowContrast(d.color)
            ? resolveCssVar('--rd-text-secondary', '#888')
            : d.color;
          return {
            name: d.label,
            value: d.value,
            itemStyle: { color: d.color },
            emphasis: { itemStyle: { color: d.color, opacity: 1, shadowBlur: 0 } },
            label: { color: labelColor },
            labelLine: { lineStyle: { color: d.color } },
          };
        }),
        z: 2,
      },
    ],
    graphic: [
      {
        type: 'text' as const,
        left: 'center',
        top: 'middle',
        style: {
          text: formatCompact(total),
          fontSize: 40,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500 as const,
          fill: resolveCssVar('--rd-text-accent', '#D32D2F'),
        },
        z: 10,
      },
    ],
  };
}
